// src/utils/SeoTagsManager.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { getFeefoDetails } from '../api/apiServices';

// FAQ data — used for FAQPage schema on vertical pages
import funeralFAQ from '../json/components/HomePages/FuneralInsurance/accordionFAQData.json';
import lifeFAQ from '../json/components/HomePages/LifeInsurance/accordionFAQData.json';
import medicalAidFAQ from '../json/components/HomePages/MedicalAid/accordionFAQData.json';
import petFAQ from '../json/components/HomePages/PetInsurance/accordionFAQData.json';
import gapCoverFAQ from '../json/components/HomePages/GapCover/accordionFAQData.json';
import medicalInsuranceFAQ from '../json/components/HomePages/MedicalInsurance/accordionFAQData.json';
import personalLoansFAQ from '../json/components/HomePages/PersonalLoans/accordionFAQData.json';
import debtCounsellingFAQ from '../json/components/HomePages/DebtCounselling/accordionFAQData.json';
import scratchAndDentFAQ from '../json/components/HomePages/DentsAndScratches/accordionFAQData.json';
import tyreAndRimFAQ from '../json/components/HomePages/TyreAndRim/accordionFAQData.json';
import vehicleTrackingFAQ from '../json/components/HomePages/VehicleTracking/accordionFAQData.json';
import servicePlanFAQ from '../json/components/HomePages/ServicePlans/accordionFAQData.json';
import homeContentsFAQ from '../json/components/HomePages/HomeContentsInsurance/accordionFAQData.json';
import buildingsInsuranceFAQ from '../json/components/HomePages/BuildingInsurance/accordionFAQData.json';
import businessInsuranceFAQ from '../json/components/HomePages/BusinessInsurance/accordionFAQData.json';
import motorcycleFAQ from '../json/components/HomePages/MotorCycleInsurance/accordionFAQData.json';
import businessLegalCoverFAQ from '../json/components/HomePages/BusinessLegalCover/accordionFAQData.json';
import commercialPropertyFAQ from '../json/components/HomePages/CommercialProperty/accordionFAQData.json';
import professionalLiabilityFAQ from '../json/components/HomePages/ProfessionalLiability/accordionFAQData.json';
import homePageFAQ from '../json/components/HomePages/HomePage/homePageFaqData.json';

// Car Insurance page-component data — the flagship page derives its richer
// schema (FAQ, HowTo steps, cover-tier OfferCatalog, review) from the same
// JSON that drives its on-page components, rather than duplicating the content.
import carFAQ from '../json/components/HomePages/CarInsurance/accordionFAQData.json';
import carHowToData from '../json/components/HomePages/CarInsurance/keyPointVerticalNumericData.json';
import carOfferTiers from '../json/components/HomePages/CarInsurance/threeColumnCardData.json';
import carReviews from '../json/components/HomePages/CarInsurance/customerReviewsData.json';

// HowTo steps data — used for HowTo schema on verticals with visible steps
import personalLoansSteps from '../json/components/HomePages/PersonalLoans/sectionStepsData.json';
import businessInsuranceSteps from '../json/components/HomePages/BusinessInsurance/sectionStepsData.json';
import businessLegalCoverSteps from '../json/components/HomePages/BusinessLegalCover/sectionStepsData.json';
import commercialPropertySteps from '../json/components/HomePages/CommercialProperty/sectionStepsData.json';
import professionalLiabilitySteps from '../json/components/HomePages/ProfessionalLiability/sectionStepsData.json';
import eventLiabilityCoverSteps from '../json/components/HomePages/EventLiabilityCover/sectionStepsData.json';

// =========================
// FEEFO LIVE DATA
// =========================

// Single shared promise so all instances share one in-flight request
let feefoCache = null;
let feefoPromise = null;

function loadFeefo() {
  if (feefoPromise) return feefoPromise;
  feefoPromise = getFeefoDetails()
    .then((data) => {
      feefoCache = data;
      return data;
    })
    .catch(() => {
      return null;
    });
  return feefoPromise;
}

function buildAggregateRating(fallback, feefo) {
  if (!feefo) return fallback;

  return {
    '@type': 'AggregateRating',
    ratingValue: String(feefo.AverageScore ?? fallback?.ratingValue ?? '4.5'),
    reviewCount: String(feefo.TotalReviews ?? fallback?.reviewCount ?? '0'),
    ...(fallback?.bestRating !== undefined && { bestRating: '5' }),
    ...(fallback?.worstRating !== undefined && { worstRating: '1' }),
  };
}

function buildFeefoReview(fallback, feefo) {
  if (!feefo?.LatestReviews?.length) return fallback;

  const review =
    feefo.LatestReviews.find((r) => r.Rating === 5) || feefo.LatestReviews[0];

  if (!review) return fallback;

  return {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: String(review.Rating),
      bestRating: '5',
      worstRating: '1',
    },
    author: {
      '@type': 'Person',
      name: review.Customer || 'Anonymous',
    },
    reviewBody: review.ReviewText || '',
  };
}

function patchSchemaWithFeefo(schema, feefo) {
  if (!feefo) return schema;

  const patched = { ...schema };

  if (patched.aggregateRating) {
    patched.aggregateRating = buildAggregateRating(
      patched.aggregateRating,
      feefo
    );
  }

  if (patched.review) {
    patched.review = buildFeefoReview(patched.review, feefo);
  }

  return patched;
}

/**
 * =========================
 * LINKED GRAPH APPROACH
 * =========================
 *
 * Every page outputs a SINGLE <script type="application/ld+json"> containing a
 * JSON-LD @graph array. Entity relationships are expressed via @id references
 * (like foreign keys) rather than duplicating inline objects.
 *
 * Stable entity anchors:
 *   Organisation  → https://www.hippo.co.za/#organization
 *   WebSite       → https://www.hippo.co.za/#website
 *   LocalBusiness → https://www.hippo.co.za/#localbusiness
 *   WebPage       → <canonical-url>#webpage   (auto-generated per page)
 *   Service       → <canonical-url>#service   (added for pages with rich schemas)
 *
 * Google can follow the @id references to build a Knowledge Graph of Hippo's
 * entity relationships — without needing to re-parse duplicate inline objects.
 */

const BASE_URL =
  process.env.REACT_APP_CANONICAL_BASE_URL || 'https://www.hippo.co.za';

// =========================
// SITE-WIDE ENTITY NODES
// These three nodes appear on every indexed page.
// =========================

const WEBSITE_NODE = {
  '@type': 'WebSite',
  '@id': `${BASE_URL}/#website`,
  name: 'Hippo.co.za',
  url: BASE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BASE_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

/**
 * Single merged entity combining FinancialService + LocalBusiness under one @id.
 * This allows Google to associate all page-level schemas (Service, BlogPosting, etc.)
 * with the same canonical organisation entity via { "@id": ".../#organization" }.
 */
const ORG_NODE = {
  '@type': ['FinancialService', 'LocalBusiness'],
  '@id': `${BASE_URL}/#organization`,
  name: 'Hippo Comparative Services',
  legalName: 'Hippo Comparative Services (Pty) Ltd',
  alternateName: 'Hippo.co.za',
  url: BASE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${BASE_URL}/logo.png`,
    width: 250,
    height: 60,
  },
  description:
    "South Africa's leading insurance comparison platform helping consumers find affordable car, home, life, and business insurance from top-rated providers.",
  foundingDate: '2007',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Block A, Steyn City Capital Park, Erling Road, Riverglen',
    addressLocality: 'Dainfern',
    addressRegion: 'Gauteng',
    postalCode: '2191',
    addressCountry: 'ZA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -26.0145,
    longitude: 28.0567,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
      opens: '08:00',
      closes: '19:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Friday',
      opens: '08:00',
      closes: '17:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '08:00',
      closes: '13:00',
    },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    telephone: '+27-10-491-0063',
    areaServed: 'ZA',
    availableLanguage: ['en', 'af'],
  },
  sameAs: [
    'https://www.facebook.com/HippoSA',
    'https://www.linkedin.com/company/hippo-comparative-services-pty-ltd',
    'https://twitter.com/HippoSA',
    'https://www.instagram.com/hippo.co.za',
    'https://www.youtube.com/@HippoSA',
    'https://www.tiktok.com/@hippo.co.za',
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.5',
    reviewCount: '20704',
    bestRating: '5',
    worstRating: '1',
  },
  review: {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
      worstRating: '1',
    },
    author: {
      '@type': 'Person',
      name: 'Anonymous',
    },
    reviewBody: '',
  },
  additionalProperty: {
    '@type': 'PropertyValue',
    name: 'FSP Number',
    value: '16357',
  },
};

/**
 * Two nodes present on every indexed page — WebSite (for sitelinks searchbox)
 * and the merged Organisation entity.
 */
const SITE_GRAPH_NODES = [WEBSITE_NODE, ORG_NODE];

// =========================
// AUTO WEBPAGE NODE BUILDER
// =========================
/**
 * Generates a standard WebPage node for any page, linked back to the
 * site-wide WebSite and Organization anchors. If an aboutId is supplied,
 * the page is additionally linked to its primary Service entity.
 */
function buildWebPageNode(url, name, description, aboutId = null, opts = {}) {
  const node = {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    name,
    description,
    url,
    inLanguage: 'en-ZA',
    isPartOf: { '@type': 'WebSite', name: 'Hippo', url: BASE_URL },
    publisher: { '@id': `${BASE_URL}/#organization` },
  };
  if (aboutId) node.about = { '@id': aboutId };
  // Optional enrichments (used by pages that supply their own WebPage node).
  if (opts.audience) node.audience = opts.audience;
  if (opts.datePublished) node.datePublished = opts.datePublished;
  if (opts.dateModified) node.dateModified = opts.dateModified;
  if (opts.breadcrumbId) node.breadcrumb = { '@id': opts.breadcrumbId };
  return node;
}

// =========================
// REUSABLE SCHEMA BUILDERS
// =========================

function stripTags(html) {
  return typeof html === 'string' ? html.replace(/<[^>]*>/g, '') : '';
}

/**
 * Build an OfferCatalog from cover-tier card data (e.g. threeColumnCardData.json,
 * shape `{ cards: [{ title, body }] }`). Attached to a Service node as
 * `hasOfferCatalog`. Card titles may contain newlines (used for on-page layout)
 * which are collapsed to single spaces here.
 */
function buildOfferCatalog(name, cardData) {
  if (!cardData?.cards?.length) return null;
  return {
    '@type': 'OfferCatalog',
    name,
    itemListElement: cardData.cards.map((card) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: stripTags(card.title || '')
          .replace(/\s+/g, ' ')
          .trim(),
        description: stripTags(card.body || ''),
      },
    })),
  };
}

function buildVerticalServiceNode(url, verticalName, opts = {}) {
  // NOTE: aggregateRating/review are intentionally NOT placed on the Service
  // node. Google does not support review snippets on the `Service` type and
  // flags them with "Invalid object type for field '<parent_node>'". Ratings
  // live on the vertical Product node (a supported type) and the site-wide
  // Organization/LocalBusiness node instead.
  const node = {
    '@type': 'Service',
    '@id': `${url}/#service`,
    serviceType: `${verticalName} Comparison`,
    name: `Compare ${verticalName}`,
    provider: { '@id': `${BASE_URL}/#organization` },
    areaServed: { '@type': 'Country', name: 'South Africa' },
  };
  if (opts.description) node.description = opts.description;
  if (opts.availableChannel) node.availableChannel = opts.availableChannel;
  if (opts.termsOfService) node.termsOfService = opts.termsOfService;
  if (opts.hasOfferCatalog) node.hasOfferCatalog = opts.hasOfferCatalog;
  if (opts.offers) node.offers = opts.offers;
  return node;
}

// Category landing pages — the second-level breadcrumb for product pages.
// URLs are stored without a trailing slash to match the Service/@id anchors
// used elsewhere in the graph; buildBreadcrumbNode adds the trailing slash to
// the breadcrumb `item` URLs so they line up with the page canonicals.
const CATEGORY = {
  motor: { name: 'Motor', url: `${BASE_URL}/motor` },
  insurance: { name: 'Insurance', url: `${BASE_URL}/insurance` },
  health: { name: 'Health', url: `${BASE_URL}/health` },
  money: { name: 'Money', url: `${BASE_URL}/money` },
  internet: { name: 'Internet', url: `${BASE_URL}/internet` },
};

// Business Insurance is the parent crumb for its nested sub-products
// (legal cover, professional liability, event liability, etc.).
const BUSINESS_INSURANCE_CRUMB = {
  name: 'Business Insurance',
  url: `${BASE_URL}/business-insurance-quote`,
};

/**
 * Build a BreadcrumbList for a product page. `trail` is the ordered list of
 * intermediate crumbs between Home and the page itself — e.g. the category
 * landing page ([CATEGORY.insurance]) plus, for nested verticals, a parent
 * product ([CATEGORY.insurance, BUSINESS_INSURANCE_CRUMB]). Positions are
 * assigned automatically: Home is 1, the page leaf is last. Category and leaf
 * `item` URLs carry a trailing slash to match the page canonicals; Home stays
 * as the bare domain.
 */
function buildBreadcrumbNode(url, verticalName, trail = []) {
  const withSlash = (u) => (u.endsWith('/') ? u : `${u}/`);
  const middle = trail.map((crumb, i) => ({
    '@type': 'ListItem',
    position: i + 2,
    name: crumb.name,
    item: withSlash(crumb.url),
  }));
  return {
    '@type': 'BreadcrumbList',
    '@id': `${url}/#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      ...middle,
      {
        '@type': 'ListItem',
        position: trail.length + 2,
        name: verticalName,
        item: withSlash(url),
      },
    ],
  };
}

function buildFAQNode(url, faqs, opts = {}) {
  const node = {
    '@type': 'FAQPage',
    '@id': `${url}/#faq`,
    mainEntity: faqs.map(({ title, description }) => ({
      '@type': 'Question',
      name: stripTags(title),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripTags(description),
      },
    })),
  };
  if (opts.mainEntityOfPage) {
    node.mainEntityOfPage = { '@id': opts.mainEntityOfPage };
  }
  return node;
}

/**
 * Build a fallback Review node from customer-review card data
 * (shape `{ reviews: [{ stars, text, name }] }`). This is only the static
 * fallback — patchSchemaWithFeefo() swaps in live Feefo data at render time.
 */
function buildReviewFromData(reviewsData) {
  const r =
    reviewsData?.reviews?.find((x) => x.stars === 5) ||
    reviewsData?.reviews?.[0];
  if (!r) return null;
  return {
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: String(r.stars ?? '5'),
      bestRating: '5',
      worstRating: '1',
    },
    author: { '@type': 'Person', name: r.name || 'Anonymous' },
    reviewBody: stripTags(r.text || ''),
  };
}

function buildVerticalProductNode(url, verticalName, lowPrice, opts = {}) {
  return {
    '@type': 'Product',
    '@id': `${url}/#product`,
    name: opts.name || `Compare ${verticalName}`,
    ...(opts.description && { description: opts.description }),
    brand: {
      '@type': 'Brand',
      name: 'Hippo',
    },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'ZAR',
      lowPrice: String(lowPrice),
      offerCount: '1',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.5',
      reviewCount: '20704',
      bestRating: '5',
      worstRating: '1',
    },
    review: opts.review || {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5',
        worstRating: '1',
      },
      author: {
        '@type': 'Person',
        name: 'Anonymous',
      },
      reviewBody: '',
    },
  };
}

function buildHowToNode(url, verticalName, steps, opts = {}) {
  const node = {
    '@type': 'HowTo',
    '@id': `${url}/#howto`,
    name: opts.name || `How to Compare ${verticalName}`,
    ...(opts.description && { description: opts.description }),
    ...(opts.totalTime && { totalTime: opts.totalTime }),
    ...(opts.estimatedCost && { estimatedCost: opts.estimatedCost }),
    ...(opts.tool && { tool: opts.tool }),
    // Steps may come from sectionStepsData ({heading}) or keyPoint data ({title}).
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: stripTags(s.heading || s.title),
      text: stripTags(s.text),
      url: `${url}#step${i + 1}`,
    })),
  };
  return node;
}

// Canonical provider registry — name + URL for JSON-LD ItemList nodes
const PROVIDER_REGISTRY = {
  // Short-Term Insurance
  miway: { name: 'MiWay Insurance', url: 'https://www.miway.co.za' },
  pineapple: {
    name: 'Pineapple Insurance',
    url: 'https://www.pineapple.co.za',
  },
  ag: { name: 'Auto&General', url: 'https://www.autogen.co.za/' },
  budget: {
    name: 'Budget Insurance',
    url: 'https://www.budgetinsurance.co.za',
  },
  dialdirect: { name: 'Dial Direct', url: 'https://www.dialdirect.co.za' },
  ffw: { name: 'First For Women', url: 'https://www.firstforwomen.co.za' },
  virseker: { name: 'Virseker', url: 'https://www.virseker.co.za' },
  iwyze: { name: 'iWYZE', url: 'https://www.iwyze.co.za' },
  omi: { name: 'Old Mutual Insure', url: 'https://www.oldmutual.co.za' },
  nedbank: { name: 'Nedbank', url: 'https://personal.nedbank.co.za/home.html' },
  absa: { name: 'Absa', url: 'https://www.absa.co.za/personal/' },
  kingprice: {
    name: 'King Price Insurance',
    url: 'https://www.kingprice.co.za/home',
  },
  oldmutual: { name: 'Old Mutual', url: 'https://www.oldmutual.co.za' },
  // Life, Medical & Funeral
  differentlife: {
    name: 'Different Life',
    url: 'https://www.differentlife.co.za',
  },
  momentum: { name: 'Momentum Life', url: 'https://www.momentum.co.za' },
  momentuminsure: {
    name: 'Momentum Insure',
    url: 'https://www.momentum.co.za',
  },
  onelife: { name: '1Life', url: 'https://www.1life.co.za' },
  dischemlife: { name: 'Dis-Chem Life', url: 'https://www.dischem.co.za' },
  capitallegacy: {
    name: 'Capital Legacy',
    url: 'https://www.capitallegacy.co.za',
  },
  fnb: { name: 'FNB', url: 'https://www.fnb.co.za' },
  simply: { name: 'Simply', url: 'https://www.simply.co.za' },
  bestmed: { name: 'BestMed', url: 'https://www.bestmed.co.za' },
  bonitas: { name: 'Bonitas', url: 'https://www.bonitas.co.za' },
  compcare: { name: 'CompCare', url: 'https://www.compcare.co.za' },
  discovery: { name: 'Discovery Health', url: 'https://www.discovery.co.za' },
  fedhealth: { name: 'Fedhealth', url: 'https://www.fedhealth.co.za' },
  keyhealth: { name: 'Keyhealth', url: 'https://www.keyhealthmedical.co.za/' },
  medihelp: { name: 'Medihelp', url: 'https://www.medihelp.co.za' },
  medshield: { name: 'Medshield', url: 'https://www.medshield.co.za' },
  momentumhealth: {
    name: 'Momentum Health',
    url: 'https://www.momentumhealth.co.za',
  },
  profmed: { name: 'Profmed', url: 'https://www.profmed.co.za' },
  tra: { name: 'Total Risk Administrators', url: 'https://totalrisksa.co.za/' },
  flexi: { name: 'Flexi-Med', url: 'https://www.flexi-med.co.za/' },
  oneplan: { name: 'Oneplan', url: 'https://www.oneplan.co.za' },
  // Financial
  directaxis: { name: 'DirectAxis', url: 'https://www.directaxis.co.za' },
  cashpower: {
    name: 'CashPower',
    url: 'https://www.cashpowerloans.co.za/',
  },
  africanbank: {
    name: 'African Bank',
    url: 'https://www.africanbank.co.za/en/home/',
  },
  sanlam: { name: 'Sanlam', url: 'https://www.sanlam.co.za' },
  omf: { name: 'Old Mutual Finance', url: 'https://www.oldmutual.co.za' },
  fincheck: { name: 'Fincheck', url: 'https://www.fincheck.co.za' },
  debtbusters: { name: 'DebtBusters', url: 'https://www.debtbusters.co.za' },
  // VAPs
  hepstar: { name: 'Hepstar', url: 'https://www.hepstar.com' },
  netstar: { name: 'Netstar', url: 'https://www.netstar.co.za' },
  fidelity: {
    name: 'Fidelity Services Group',
    url: 'https://fidelity-services.com/',
  },
  tracker: { name: 'Tracker', url: 'https://www.tracker.co.za' },
  mixtelematics: {
    name: 'Mix Telematics',
    url: 'https://www.mixtelematics.com/za/',
  },
  dotsure: { name: 'Dotsure', url: 'https://www.dotsure.co.za' },
  onepet: { name: 'OnePet', url: 'https://www.onepet.co.za' },
  blackcygnet: { name: 'Blackcygnet', url: 'https://www.blackcygnet.co.za' },
  fibrecompare: {
    name: 'Fibre Compare',
    url: 'https://www.fibrecompare.co.za',
  },
  // Car VAPs
  genric: { name: 'Genric Insurance', url: 'https://www.genric.co.za' },
};

function buildProviderListNode(
  pageUrl,
  providers,
  { name = 'Partners', description } = {}
) {
  const items = providers.map((key, i) => {
    const p = PROVIDER_REGISTRY[key];
    return {
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Organization',
        name: p.name,
        ...(p.url && { url: p.url }),
      },
    };
  });
  return {
    '@type': 'ItemList',
    '@id': `${pageUrl}/#providers`,
    name,
    ...(description && { description }),
    numberOfItems: items.length,
    itemListElement: items,
  };
}

/**
 * Build all schema nodes for a product vertical page.
 * Includes Service + Breadcrumb always; FAQ, HowTo, and ItemList only when data exists.
 *
 * `opts` (all optional) lets a richer page (e.g. Car Insurance) enrich the
 * generated nodes without a bespoke code path:
 *   opts.service      → { description, availableChannel, termsOfService, offers }
 *   opts.offerCatalog → { name, cardData } to attach hasOfferCatalog to the Service
 *   opts.product      → { name, description, reviewsData } for the Product node
 *   opts.howTo        → { name, description, tool, totalTime, estimatedCost }
 *   opts.faq          → { mainEntityOfPage }
 *   opts.breadcrumb   → array of intermediate crumbs between Home and this page,
 *                       e.g. [CATEGORY.insurance] or
 *                       [CATEGORY.insurance, BUSINESS_INSURANCE_CRUMB]
 *   opts.webPage      → { name, description, aboutId, audience, datePublished,
 *                         dateModified, breadcrumbId } → emits a WebPage node
 */
function buildVerticalNodes(
  url,
  verticalName,
  faqData,
  stepsData,
  providers,
  lowPrice,
  opts = {}
) {
  const serviceOpts = { ...(opts.service || {}) };
  if (opts.offerCatalog) {
    serviceOpts.hasOfferCatalog = buildOfferCatalog(
      opts.offerCatalog.name,
      opts.offerCatalog.cardData
    );
  }

  const nodes = [
    buildVerticalServiceNode(url, verticalName, serviceOpts),
    buildBreadcrumbNode(url, verticalName, opts.breadcrumb),
  ];
  if (lowPrice != null) {
    const productOpts = { ...(opts.product || {}) };
    if (productOpts.reviewsData) {
      productOpts.review = buildReviewFromData(productOpts.reviewsData);
      delete productOpts.reviewsData;
    }
    nodes.push(
      buildVerticalProductNode(url, verticalName, lowPrice, productOpts)
    );
  }
  if (faqData?.faq?.length) {
    nodes.push(buildFAQNode(url, faqData.faq, opts.faq));
  }
  if (stepsData?.steps?.length) {
    nodes.push(buildHowToNode(url, verticalName, stepsData.steps, opts.howTo));
  }
  if (providers?.length) {
    nodes.push(buildProviderListNode(url, providers, opts.providerList));
  }
  if (opts.webPage) {
    nodes.push(
      buildWebPageNode(
        url,
        opts.webPage.name,
        opts.webPage.description,
        opts.webPage.aboutId,
        opts.webPage
      )
    );
  }
  return nodes;
}

// =========================
// VERTICAL SCHEMA MAP
// Auto-injected by getConfig for verticals without explicit ldNodes.
// =========================
// Shared STI provider set — Car, Bike, Home, Buildings, Combo
const STI_PROVIDERS = [
  'miway',
  'pineapple',
  'ag',
  'budget',
  'dialdirect',
  'ffw',
  'virseker',
  'iwyze',
  'omi',
  'nedbank',
  'absa',
  'kingprice',
];

const VERTICAL_SCHEMA = {
  // Flagship page — richer schema, but built through the same factory as every
  // other vertical. The extra content (cover-tier OfferCatalog, HowTo steps,
  // FAQ, review) is derived from the page's own component JSON; static fields
  // that aren't in that JSON (HowTo tool/time/cost, service channel/terms,
  // WebPage audience/dates) are passed as `opts`.
  CarInsurance: buildVerticalNodes(
    `${BASE_URL}/car-insurance-quote`,
    'Car Insurance',
    carFAQ,
    carHowToData,
    [
      'kingprice',
      'budget',
      'ag',
      'ffw',
      'miway',
      'dialdirect',
      'omi',
      'pineapple',
      'iwyze',
      'nedbank',
      'absa',
      'virseker',
      'momentuminsure',
    ],
    '89',
    {
      breadcrumb: [CATEGORY.insurance],
      service: {
        description:
          'Free car insurance comparison service helping South Africans find affordable comprehensive, third-party fire and theft, and third-party only cover from 13 top insurers.',
        availableChannel: {
          '@type': 'ServiceChannel',
          serviceUrl: `${BASE_URL}/car-insurance-quote`,
          serviceType: 'Online Service',
        },
        termsOfService: `${BASE_URL}/terms-and-conditions`,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'ZAR',
          description:
            'Free comparison service with no hidden fees or obligations',
        },
      },
      offerCatalog: { name: 'Car Insurance Products', cardData: carOfferTiers },
      product: {
        description:
          'Compare car insurance quotes from 13 South African insurers in 2 minutes. Free, no obligation, and trusted by 20,704+ customers.',
        reviewsData: carReviews,
      },
      howTo: {
        name: 'How to Compare Car Insurance Quotes in South Africa',
        description:
          'Get personalized car insurance quotes from 13 South African insurers in 5 easy steps. Compare premiums, excess, and benefits side-by-side in just 2 minutes.',
        totalTime: 'PT2M',
        estimatedCost: {
          '@type': 'MonetaryAmount',
          currency: 'ZAR',
          value: '0',
        },
        tool: [
          { '@type': 'HowToTool', name: 'South African ID Number' },
          { '@type': 'HowToTool', name: 'Vehicle Registration Details' },
        ],
      },
      faq: { mainEntityOfPage: `${BASE_URL}/car-insurance-quote#webpage` },
      providerList: {
        name: 'South African Car Insurance Providers',
        description:
          "Compare quotes from South Africa's top-rated car insurance companies",
      },
      webPage: {
        name: 'Compare Car Insurance Quotes and Save with Hippo',
        description:
          "Get instant car insurance quotes from 13 of SA's top insurers in 2 minutes. Compare Momentum, Budget, Discovery & more. Save up to R14,000/year. Free, no obligation quotes.",
        aboutId: `${BASE_URL}/car-insurance-quote/#service`,
        breadcrumbId: `${BASE_URL}/car-insurance-quote/#breadcrumb`,
        audience: {
          '@type': 'Audience',
          audienceType: 'South African car owners seeking affordable insurance',
          geographicArea: { '@type': 'Country', name: 'South Africa' },
        },
        datePublished: '2020-01-15',
        dateModified: '2025-01-14',
      },
    }
  ),

  // Core journeys
  FuneralInsurance: buildVerticalNodes(
    `${BASE_URL}/funeral-insurance-quote`,
    'Funeral Cover',
    funeralFAQ,
    null,
    [
      'fnb',
      'differentlife',
      'onelife',
      'dischemlife',
      'oldmutual',
      'simply',
      'ag',
      'budget',
      'dialdirect',
      'ffw',
    ],
    '42',
    { breadcrumb: [CATEGORY.insurance] }
  ),
  LifeInsurance: buildVerticalNodes(
    `${BASE_URL}/life-insurance-quote`,
    'Life Insurance',
    lifeFAQ,
    null,
    [
      'ag',
      'dialdirect',
      'ffw',
      'budget',
      'differentlife',
      'momentum',
      'onelife',
      'dischemlife',
      'capitallegacy',
      'oldmutual',
      'iwyze',
    ],
    '62',
    { breadcrumb: [CATEGORY.insurance] }
  ),
  MedicalAid: buildVerticalNodes(
    `${BASE_URL}/medical-aid-quote`,
    'Medical Aid',
    medicalAidFAQ,
    null,
    [
      'bestmed',
      'bonitas',
      'compcare',
      'discovery',
      'fedhealth',
      'keyhealth',
      'medihelp',
      'medshield',
      'momentumhealth',
      'profmed',
    ],
    '645',
    { breadcrumb: [CATEGORY.health] }
  ),
  PetInsurance: buildVerticalNodes(
    `${BASE_URL}/pet-insurance-quote`,
    'Pet Insurance',
    petFAQ,
    null,
    ['ag', 'ffw', 'virseker', 'dotsure', 'onepet'],
    '80',
    { breadcrumb: [CATEGORY.insurance] }
  ),

  // Extended journeys
  MedicalGapCover: buildVerticalNodes(
    `${BASE_URL}/medical-gap-cover-quote`,
    'Medical Gap Cover',
    gapCoverFAQ,
    null,
    ['ag', 'tra'],
    '99',
    { breadcrumb: [CATEGORY.health] }
  ),
  MedicalInsurance: buildVerticalNodes(
    `${BASE_URL}/medical-insurance-quotes`,
    'Medical Insurance',
    medicalInsuranceFAQ,
    null,
    ['flexi', 'oneplan'],
    '500',
    { breadcrumb: [CATEGORY.health] }
  ),
  TravelInsurance: buildVerticalNodes(
    `${BASE_URL}/travel-insurance`,
    'Travel Insurance',
    null,
    null,
    ['hepstar'],
    '249',
    { breadcrumb: [CATEGORY.insurance] }
  ),
  MotorWarranty: buildVerticalNodes(
    `${BASE_URL}/motor-warranty`,
    'Motor Warranty',
    null,
    null,
    ['genric', 'ag', 'dialdirect', 'ffw', 'budget', 'dotsure', 'kingprice'],
    '79',
    { breadcrumb: [CATEGORY.motor] }
  ),
  PersonalLoans: buildVerticalNodes(
    `${BASE_URL}/personal-loans`,
    'Personal Loans',
    personalLoansFAQ,
    personalLoansSteps,
    [
      'directaxis',
      'cashpower',
      'africanbank',
      'nedbank',
      'sanlam',
      'omf',
      'fincheck',
    ],
    '2000',
    { breadcrumb: [CATEGORY.money] }
  ),
  DebtCounselling: buildVerticalNodes(
    `${BASE_URL}/debt-counselling`,
    'Debt Counselling',
    debtCounsellingFAQ,
    null,
    ['debtbusters'],
    null,
    { breadcrumb: [CATEGORY.money] }
  ),
  ScratchAndDent: buildVerticalNodes(
    `${BASE_URL}/dents-and-scratches-quote`,
    'Scratch & Dent Cover',
    scratchAndDentFAQ,
    null,
    ['genric', 'ag', 'dialdirect', 'ffw', 'budget'],
    '99',
    { breadcrumb: [CATEGORY.motor] }
  ),
  TyreAndRimGuard: buildVerticalNodes(
    `${BASE_URL}/tyre-and-rim-guard`,
    'Tyre & Rim Cover',
    tyreAndRimFAQ,
    null,
    ['genric', 'ag', 'dialdirect', 'ffw', 'budget'],
    '66',
    { breadcrumb: [CATEGORY.motor] }
  ),
  CarTracker: buildVerticalNodes(
    `${BASE_URL}/vehicle-tracking-quote`,
    'Vehicle Tracking',
    vehicleTrackingFAQ,
    null,
    ['netstar', 'fidelity', 'tracker', 'mixtelematics'],
    '89',
    { breadcrumb: [CATEGORY.motor] }
  ),
  ServicePlan: buildVerticalNodes(
    `${BASE_URL}/service-plan-quotes`,
    'Car Service Plan',
    servicePlanFAQ,
    null,
    ['genric', 'budget'],
    '419',
    { breadcrumb: [CATEGORY.motor] }
  ),
  HomeContents: buildVerticalNodes(
    `${BASE_URL}/home-insurance-quote`,
    'Home Insurance',
    homeContentsFAQ,
    null,
    STI_PROVIDERS,
    null,
    { breadcrumb: [CATEGORY.insurance] }
  ),
  BuildingsInsurance: buildVerticalNodes(
    `${BASE_URL}/buildings-insurance-quote`,
    'Buildings Insurance',
    buildingsInsuranceFAQ,
    null,
    STI_PROVIDERS,
    null,
    { breadcrumb: [CATEGORY.insurance] }
  ),
  BusinessInsurance: buildVerticalNodes(
    `${BASE_URL}/business-insurance-quote`,
    'Business Insurance',
    businessInsuranceFAQ,
    businessInsuranceSteps,
    ['ag', 'budget', 'dialdirect', 'ffw', 'virseker', 'oldmutual'],
    null,
    { breadcrumb: [CATEGORY.insurance] }
  ),
  FibreDeals: buildVerticalNodes(
    `${BASE_URL}/compare-fibre-deals`,
    'Fibre Deals',
    null,
    null,
    ['fibrecompare'],
    null,
    { breadcrumb: [CATEGORY.internet] }
  ),
  MotorcycleInsurance: buildVerticalNodes(
    `${BASE_URL}/motorcycle-insurance-quote`,
    'Motorcycle Insurance',
    motorcycleFAQ,
    null,
    STI_PROVIDERS,
    null,
    { breadcrumb: [CATEGORY.insurance] }
  ),
  CombinedQuotes: buildVerticalNodes(
    `${BASE_URL}/online-insurance-quote`,
    'Online Insurance',
    null,
    null,
    STI_PROVIDERS,
    null,
    { breadcrumb: [CATEGORY.insurance] }
  ),

  // Content pages with product-style schema (nested under Business Insurance)
  BusinessLegalCover: buildVerticalNodes(
    `${BASE_URL}/business-insurance-quote/business-legal-cover`,
    'Business Legal Cover',
    businessLegalCoverFAQ,
    businessLegalCoverSteps,
    null,
    null,
    { breadcrumb: [CATEGORY.insurance, BUSINESS_INSURANCE_CRUMB] }
  ),
  CarAndCommercialProperty: buildVerticalNodes(
    `${BASE_URL}/business-insurance-quote/car-and-commercial-property-insurance`,
    'Car & Commercial Property Insurance',
    commercialPropertyFAQ,
    commercialPropertySteps,
    null,
    null,
    { breadcrumb: [CATEGORY.insurance, BUSINESS_INSURANCE_CRUMB] }
  ),
  ProfessionalLiability: buildVerticalNodes(
    `${BASE_URL}/business-insurance-quote/professional-liability-insurance-cover`,
    'Professional Indemnity Insurance',
    professionalLiabilityFAQ,
    professionalLiabilitySteps,
    null,
    null,
    { breadcrumb: [CATEGORY.insurance, BUSINESS_INSURANCE_CRUMB] }
  ),
  EventLiabilityCover: buildVerticalNodes(
    `${BASE_URL}/business-insurance-quote/event-liability-insurance`,
    'Event Liability Cover',
    null,
    eventLiabilityCoverSteps,
    null,
    null,
    { breadcrumb: [CATEGORY.insurance, BUSINESS_INSURANCE_CRUMB] }
  ),
};

/**
 * =========================
 * MASTER TAG REGISTRY
 * =========================
 *
 * extra.ldNodes — page-specific schema nodes to merge into the @graph.
 *                 These should NOT include @context (that belongs on the wrapper).
 *                 SITE_GRAPH_NODES are added automatically by render().
 *
 * extra.webPageNode — set to false to suppress the auto-generated WebPage node
 *                     (useful when the page supplies its own explicit WebPage node).
 */

const TAGS = {
  // --- Home Page ---
  Home: {
    title: 'Compare Quotes | Insurance, Medical Aid and Loans | Hippo',
    description:
      'Compare quotes for car insurance, medical aid, personal loans and more with Hippo. Fast, free online comparison so you can choose the right cover and save.',
    keywords:
      'insurance, car insurance, car insurance quotes, online insurance quote, cheapest car insurance quotes, best car insurance south africa',
    robots: 'INDEX, FOLLOW',
    extra: {
      themeColor: '#1f262e',
      appleIcon: `${BASE_URL}/assets/images/apple-touch-icon-02.png`,
      ogSite: 'Hippo.co.za',
      ogImage: `${BASE_URL}/assets/images/apple-touch-icon-02.png`,
      siteURL: BASE_URL,
      ldNodes: [buildFAQNode(BASE_URL, homePageFAQ.faq)],
    },
  },

  // --- Category pages ---
  InsuranceCategory: {
    title: 'Insurance | Products | Hippo',
    description:
      "Discover all of Hippo's insurance products. Compare quotes from multiple providers to find a better deal on your insurance.",
    robots: 'INDEX, FOLLOW',
  },
  HealthCategory: {
    title: 'Healthcare | Products | Hippo',
    description:
      "Discover all of Hippo's healthcare products. Compare quotes from multiple providers to find a better deal on your healthcare cover.",
    robots: 'INDEX, FOLLOW',
  },
  MoneyCategory: {
    title: 'Money | Products | Hippo',
    description:
      "Discover all of Hippo's money products. Compare multiple providers to find a better deal on your loans or debt solutions.",
    robots: 'INDEX, FOLLOW',
  },
  MotorCategory: {
    title: 'Motor | Products | Hippo',
    description:
      "Discover all of Hippo's motor products. Compare quotes from multiple providers to find a better deal on your motor insurance cover.",
    robots: 'INDEX, FOLLOW',
  },
  InternetCategory: {
    title: 'Internet | Products | Hippo',
    description:
      "Discover Hippo's home internet solutions. Check for fibre in your area and compare quotes from multiple providers to find a better deal.",
    robots: 'INDEX, FOLLOW',
  },

  // --- Blog category pages (/blog/<category>/) ---
  BlogMotorCategory: {
    title: 'Motor Blog | Hippo',
    description:
      "Read Hippo's latest guides and tips on motor insurance, warranties and more.",
    robots: 'INDEX, FOLLOW',
  },
  BlogInsuranceCategory: {
    title: 'Insurance Blog | Hippo',
    description:
      "Read Hippo's latest guides and tips on insurance products and cover.",
    robots: 'INDEX, FOLLOW',
  },
  BlogHealthCategory: {
    title: 'Health Blog | Hippo',
    description:
      "Read Hippo's latest guides and tips on medical aid, gap cover and healthcare.",
    robots: 'INDEX, FOLLOW',
  },
  BlogMoneyCategory: {
    title: 'Money Blog | Hippo',
    description:
      "Read Hippo's latest guides and tips on loans, debt and personal finance.",
    robots: 'INDEX, FOLLOW',
  },
  BlogInternetCategory: {
    title: 'Internet Blog | Hippo',
    description:
      "Read Hippo's latest guides and tips on fibre and internet deals.",
    robots: 'INDEX, FOLLOW',
  },
  TravelBlog: {
    title: 'Travel Blog | Hippo',
    description:
      "Read Hippo's latest guides and tips on travel and travel insurance.",
    robots: 'INDEX, FOLLOW',
  },

  // --- Core journeys ---
  CarInsurance: {
    title: 'Car Insurance | Compare Quotes in 2 min | Hippo',
    description:
      'Compare car insurance quotes from leading South African insurers. Get cover that suits your needs and budget with fast, free online comparison via Hippo.',
    keywords: 'car insurance',
    robots: 'INDEX, FOLLOW',
    extra: {
      // Car's schema comes from VERTICAL_SCHEMA.CarInsurance (auto-injected by
      // getConfig). It supplies its own WebPage node via the factory's
      // opts.webPage, so suppress the auto-generated one.
      webPageNode: false,
    },
  },

  FuneralInsurance: {
    title: 'Funeral Cover | Compare individual & family plans | Hippo',
    description:
      'Compare funeral cover quotes from trusted South African providers. Find affordable cover for you or your family with Hippo\u2019s free quote comparison.',
    keywords: 'funeral cover',
    robots: 'INDEX, FOLLOW',
  },
  LifeInsurance: {
    title: 'Life Insurance | Compare Quotes in 2 min | Hippo',
    description:
      'Compare life insurance quotes from leading South African providers. Find cover that fits your budget and needs with Hippo\u2019s free online comparison.',
    keywords: 'life insurance',
    robots: 'INDEX, FOLLOW',
  },
  MedicalAid: {
    title: 'Affordable Medical Aid | Compare Multiple Plans | Hippo',
    description:
      "Find better, cheaper medical aid without compromising on benefits. Compare 10 of South Africa's leading medical schemes to find the best plan for your family.",
    keywords: 'medical aid',
    robots: 'INDEX, FOLLOW',
  },
  PetInsurance: {
    title: 'Pet Insurance | Compare quotes for dogs & cats | Hippo',
    description:
      'Compare pet insurance quotes from trusted providers. Help cover vet bills with the right pet cover using Hippo\u2019s free online comparison.',
    keywords: 'pet insurance',
    robots: 'INDEX, FOLLOW',
  },

  // --- Extended journeys ---
  MedicalGapCover: {
    title: 'Gap Cover for Medical Aid Shortfalls | Compare quotes | Hippo',
    description:
      'Compare gap cover quotes from leading providers. Reduce shortfalls on in-hospital costs with fast online comparison via Hippo.',
    keywords: 'gap cover',
    robots: 'INDEX, FOLLOW',
  },
  MedicalInsurance: {
    title: 'Medical Insurance Quotes | Compare Health Cover | Hippo',
    description:
      'Compare medical insurance options from trusted providers. Find health cover that suits your needs and budget with fast, free online comparison through Hippo.',
    keywords: 'medical insurance',
    robots: 'INDEX, FOLLOW',
  },
  TravelInsurance: {
    title: 'Travel Insurance | Compare Quotes in 2 min | Hippo',
    description:
      'Compare travel insurance quotes from top providers. Get the right cover for your trip in minutes with Hippo\u2019s fast, free comparison.',
    keywords: 'travel insurance',
    robots: 'INDEX, FOLLOW',
  },
  MotorWarranty: {
    title: 'Motor Warranty Quotes | Compare Car Warranty Plans | Hippo',
    description:
      'Compare motor warranty quotes from leading providers. Cover unexpected repair costs with quick online comparison through Hippo.',
    keywords: 'motor warranty',
    robots: 'INDEX, FOLLOW',
  },
  PersonalLoans: {
    title: 'Personal Loans | Compare rates & apply online | Hippo',
    description:
      'Compare personal loan options from trusted South African providers. Fast, free online comparison so you can choose the right loan amount, term, and repayment.',
    keywords: 'personal loans',
    robots: 'INDEX, FOLLOW',
  },
  DebtCounselling: {
    title: 'Debt Counselling in South Africa | Compare Options | Hippo',
    description:
      'Need help managing debt? Compare debt counselling options from trusted providers in South Africa. Learn how debt review works and get help choosing the right next step.',
    keywords: 'debt counselling',
    robots: 'INDEX, FOLLOW',
  },
  DebtConsolidationLoans: {
    title: 'Debt Consolidation Loans | Compare Quotes | Hippo',
    description:
      'Compare debt consolidation loan options from trusted South African providers. Combine your debts into one manageable repayment with Hippo’s free comparison.',
    keywords: 'debt consolidation loans',
    robots: 'INDEX, FOLLOW',
    extra: {
      ldNodes: [
        buildBreadcrumbNode(
          `${BASE_URL}/debt-consolidation-loans`,
          'Debt Consolidation Loans',
          [CATEGORY.money]
        ),
      ],
    },
  },
  ScratchAndDent: {
    title: 'Scratch & Dent Cover | Compare Quotes in 2 min | Hippo',
    description:
      'Compare scratch and dent cover quotes from leading providers. Protect your vehicle against minor bodywork costs with fast online comparison via Hippo.',
    keywords: 'scratch and dents cover',
    robots: 'INDEX, FOLLOW',
  },
  TyreAndRimGuard: {
    title: 'Tyre & Rim Cover | Compare Quotes in 2 min | Hippo',
    description:
      'Compare tyre and rim cover quotes from leading providers. Protect against unexpected wheel and tyre damage with fast online comparison via Hippo.',
    keywords: 'tyre and rim cover',
    robots: 'INDEX, FOLLOW',
  },
  CarTracker: {
    title: 'Vehicle Tracking | Compare Quotes in 2 min | Hippo',
    description:
      'Compare vehicle tracking quotes from leading providers. Protect your car with the right tracking solution using Hippo\u2019s free online comparison.',
    keywords: 'vehicle tracking',
    robots: 'INDEX, FOLLOW',
  },
  ServicePlan: {
    title: 'Car Service Plan | Compare Quotes in 2 min | Hippo',
    description:
      'Compare car service plan options from leading providers. Choose a plan that suits your car and budget with Hippo\u2019s free quote comparison.',
    keywords: 'service plans',
    robots: 'INDEX, FOLLOW',
  },
  HomeContents: {
    title: 'Home Insurance Quotes | Compare Cover in South Africa | Hippo',
    description:
      'Compare home insurance quotes from trusted South African providers. Find cover for your home (and contents, if needed) with fast, free online comparison through Hippo.',
    keywords: 'home contents insurance',
    robots: 'INDEX, FOLLOW',
  },
  BuildingsInsurance: {
    title: 'Buildings Insurance | Compare Quotes in 2 min | Hippo',
    description:
      'Compare buildings insurance quotes from leading South African providers. Get cover that fits your home and budget in minutes with Hippo.',
    keywords: 'buildings insurance',
    robots: 'INDEX, FOLLOW',
  },
  BusinessInsurance: {
    title: 'Business Insurance | Compare Quotes in 2 min | Hippo',
    description:
      'Compare business insurance quotes from trusted South African providers. Protect your business with the right cover; quick, free comparison via Hippo.',
    keywords: 'business insurance',
    robots: 'INDEX, FOLLOW',
  },
  FibreDeals: {
    title: 'Fibre Deals | Find the best deals in your area | Hippo',
    description:
      'Compare fibre deals and internet packages from leading ISPs. Find the right speed and price for your home with Hippo\u2019s free comparison.',
    keywords: 'fibre deals',
    robots: 'INDEX, FOLLOW',
  },
  // Keyed by the flow's `product` value (see flowSlugConfig.js) rather than a
  // pathname needle, since QuestionEngine looks up SEO tags by product, not
  // pathname \u2014 this is what the /check-fibre-in-your-area/stepN pages resolve to.
  FibreInternet: {
    title: 'Fibre in my area | Compare quotes | Hippo',
    description:
      'Simply enter your address to check if any Fibre providers are servicing your area. Compare multiple fibre deals, providers and packages in just 2 minutes.',
    keywords: 'fibre in my area',
    robots: 'INDEX, FOLLOW',
  },
  MotorcycleInsurance: {
    title: 'Motorbike Insurance | Compare Quotes | Hippo',
    description:
      'Compare motorcycle insurance quotes from trusted South African providers. Find cover that suits your bike and budget with Hippo\u2019s fast, free online comparison.',
    robots: 'INDEX, FOLLOW',
  },
  CombinedQuotes: {
    title: 'Online Insurance Quotes | Compare & Save in SA | Hippo',
    description:
      'Compare insurance quotes online in minutes. Hippo helps you compare trusted providers so you can choose the right cover and save \u2014 fast and free.',
    robots: 'INDEX, FOLLOW',
  },

  // --- Results / Quote / Accounts (NOINDEX — no schema output) ---
  VehicleQuoteResults: {
    title: 'Car Insurance Quotes | Results | Hippo',
    description: 'Your personalised car insurance quote results.',
    robots: 'NOINDEX, NOFOLLOW',
  },
  LifeQuoteResults: {
    title: 'Life Insurance Quotes | Results | Hippo',
    description: 'Your personalised life insurance quote results.',
    robots: 'NOINDEX, NOFOLLOW',
  },
  FuneralQuoteResults: {
    title: 'Funeral Cover Quotes | Results | Hippo',
    description: 'Your personalised funeral cover quote results.',
    robots: 'NOINDEX, NOFOLLOW',
  },
  QuoteSelection: {
    title: 'Select Your Quote | Hippo',
    description: 'Review and select your preferred quote option.',
    robots: 'NOINDEX, NOFOLLOW',
  },
  AccountsProfile: {
    title: 'My Profile | Hippo',
    description: 'Manage your account, quotes and preferences.',
    robots: 'NOINDEX, NOFOLLOW',
  },

  // --- About page (Part 4 — static AboutPage + BreadcrumbList) ---
  AboutUs: {
    title: 'About Us | Hippo',
    description:
      "Learn about Hippo.co.za, South Africa's leading comparison platform.",
    robots: 'INDEX, FOLLOW',
    extra: {
      webPageNode: false,
      ldNodes: [
        {
          '@type': 'AboutPage',
          '@id': `${BASE_URL}/about-us/#webpage`,
          url: `${BASE_URL}/about-us`,
          name: 'About Hippo.co.za',
          description:
            "Learn about Hippo.co.za, South Africa's leading comparison platform.",
          mainEntity: { '@id': `${BASE_URL}/#organization` },
          isPartOf: { '@type': 'WebSite', name: 'Hippo', url: BASE_URL },
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${BASE_URL}/about-us/#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: BASE_URL,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'About Us',
              item: `${BASE_URL}/about-us`,
            },
          ],
        },
      ],
    },
  },

  // --- Content pages ---
  Blog: {
    title: 'Our Blog | Hippo',
    description:
      'Explore Hippo\u2019s latest guides on insurance, medical aid, loans, and personal finance. Practical tips and explainers to help you compare and choose with confidence.',
    robots: 'INDEX, FOLLOW',
  },
  BusinessLegalCover: {
    title: 'Business Legal Cover | Compare Quotes in South Africa | Hippo',
    description:
      'Compare business legal cover options from trusted providers. Protect your business with the right legal support and cover \u2014 quick, free online comparison through Hippo.',
    keywords: 'business legal cover',
    robots: 'INDEX, FOLLOW',
  },
  CarAndCommercialProperty: {
    title: 'Car & Commercial Property Insurance | Compare Quotes in SA | Hippo',
    description:
      'Compare car and commercial property insurance options from trusted providers. Find cover for your vehicles and business premises with fast, free online comparison through Hippo.',
    keywords: 'car and commercial property cover',
    robots: 'INDEX, FOLLOW',
  },
  Contact: {
    title: 'Contact Us | Hippo',
    description:
      'Contact Hippo, our team is here to assist. Call 079 482 6383 or share your details and we\u2019ll point you in the right direction.',
    robots: 'INDEX, FOLLOW',
  },
  EmployeeBenefits: {
    title: 'Medical Aid for Employees | Compare Corporate Options | Hippo',
    description:
      'Compare medical aid options for your employees. Explore plan types and benefits from trusted providers and find a solution that fits your team and budget.',
    keywords: 'employee benefits',
    robots: 'INDEX, FOLLOW',
    extra: {
      ldNodes: [
        buildBreadcrumbNode(
          `${BASE_URL}/employee-benefits-quote`,
          'Employee Benefits',
          [CATEGORY.insurance, BUSINESS_INSURANCE_CRUMB]
        ),
      ],
    },
  },
  EventLiabilityCover: {
    title: 'Event Liability Cover | Compare Quotes in South Africa | Hippo',
    description:
      'Compare event liability cover options from trusted providers. Protect your event against claims for injury or property damage — quick, free online comparison through Hippo.',
    keywords: 'event liability cover',
    robots: 'INDEX, FOLLOW',
  },
  News: {
    title: 'News & Updates | Hippo.co.za',
    description:
      'Read the latest news and updates from Hippo, including insurance and personal finance insights to help you make better decisions.',
    robots: 'INDEX, FOLLOW',
  },
  ProfessionalLiability: {
    title: 'Professional Liability Insurance | Compare Quotes | Hippo',
    description:
      'Compare professional indemnity (professional liability) insurance quotes from trusted providers. Find cover that suits your profession \u2014 quick, free comparison.',
    keywords: 'professional liability cover',
    robots: 'INDEX, FOLLOW',
  },
  Terms: {
    title: 'Terms and Conditions | Hippo.co.za',
    description:
      'Read Hippo\u2019s Terms and Conditions for using our website and services, including important legal information, limitations, and user responsibilities.',
    robots: 'INDEX, FOLLOW',
  },
};

// =========================
// GENERIC DEFAULT (fallback)
// =========================
const DEFAULT_TAG = {
  title: 'Compare Quotes | Insurance, Medical Aid and Loans | Hippo',
  description:
    'Compare quotes for car insurance, medical aid, personal loans and more with Hippo. Fast, free online comparison so you can choose the right cover and save.',
  keywords:
    'insurance, car insurance, car insurance quotes, online insurance quote, cheapest car insurance quotes, best car insurance south africa',
};

// =========================
// PATH → TAG KEY MAPPING
// =========================
const SUBSTRING_TO_KEY = [
  // Blog category pages (/blog/<category>/) must be checked before both the
  // generic category needles and the 'blog' needle below, since paths like
  // '/blog/motor/' or '/blog/insurance/' would otherwise match those first.
  ['/blog/motor', 'BlogMotorCategory'],
  ['/blog/insurance', 'BlogInsuranceCategory'],
  ['/blog/health', 'BlogHealthCategory'],
  ['/blog/money', 'BlogMoneyCategory'],
  ['/blog/internet', 'BlogInternetCategory'],
  ['/blog/travel-blog', 'TravelBlog'],

  // Category pages (must be checked before the generic 'insurance' needles
  // below since '/insurance' would otherwise never be reached). '/motor' is
  // deliberately NOT here — see below, it must come after 'motor-warranty'
  // and 'motorcycle-insurance-quote' since those routes also start with '/motor'.
  ['/insurance', 'InsuranceCategory'],
  ['/health', 'HealthCategory'],
  ['/money', 'MoneyCategory'],
  ['/internet', 'InternetCategory'],

  // Core
  ['funeral', 'FuneralInsurance'],
  ['life', 'LifeInsurance'],
  ['medical-aid', 'MedicalAid'],
  ['pet', 'PetInsurance'],
  ['car-insurance', 'CarInsurance'],

  // Extended
  ['medical-gap-cover', 'MedicalGapCover'],
  ['medical-insurance', 'MedicalInsurance'],
  ['travel-insurance', 'TravelInsurance'],
  ['motor-warranty', 'MotorWarranty'],
  ['personal-loans', 'PersonalLoans'],
  ['debt-consolidation', 'DebtConsolidationLoans'],
  ['debt-counselling', 'DebtCounselling'],
  ['dents-and-scratches', 'ScratchAndDent'],
  ['scratch', 'ScratchAndDent'],
  ['tyre-and-rim', 'TyreAndRimGuard'],
  ['vehicle-tracking', 'CarTracker'],
  ['service-plan', 'ServicePlan'],
  ['home-insurance', 'HomeContents'],
  ['home-contents', 'HomeContents'],
  ['buildings-insurance', 'BuildingsInsurance'],
  // Nested business verticals must be checked before the generic
  // 'business-insurance' needle below, since their URLs are nested
  // under /business-insurance-quote/.
  ['business-legal-cover', 'BusinessLegalCover'],
  ['car-and-commercial-property-insurance', 'CarAndCommercialProperty'],
  ['professional-liability-insurance-cover', 'ProfessionalLiability'],
  ['event-liability-insurance', 'EventLiabilityCover'],
  ['business-insurance', 'BusinessInsurance'],
  ['compare-fibre', 'FibreDeals'],
  ['fibre-deals', 'FibreDeals'],
  ['motorcycle-insurance-quote', 'MotorcycleInsurance'],
  ['online-insurance-quote', 'CombinedQuotes'],
  // Must come after 'motor-warranty' and 'motorcycle-insurance-quote' above,
  // since '/motor-warranty' and '/motorcycle-insurance-quote' also start with '/motor'.
  ['/motor', 'MotorCategory'],

  // Results / Quote / Accounts
  ['vehiclequoteresults', 'VehicleQuoteResults'],
  ['lifequoteresults', 'LifeQuoteResults'],
  ['funeralquoteresults', 'FuneralQuoteResults'],
  ['quoteselection', 'QuoteSelection'],
  ['accounts', 'AccountsProfile'],

  // About
  ['about', 'AboutUs'],

  // Content
  ['blog', 'Blog'],
  ['contact', 'Contact'],
  ['employee-benefits-quote', 'EmployeeBenefits'],
  ['news', 'News'],
  ['terms', 'Terms'],
];

// =========================
// HELPERS
// =========================
function resolveKey(identifier = '') {
  const id = String(identifier || '')
    .toLowerCase()
    .trim();

  if (id === '/' || id === '') return 'Home';

  for (const key of Object.keys(TAGS)) {
    if (id === key.toLowerCase()) return key;
  }

  for (const [needle, key] of SUBSTRING_TO_KEY) {
    if (id.includes(needle)) return key;
  }

  return null;
}

function isPlainObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge(base, override) {
  if (!override) return base;
  const out = { ...(base || {}) };

  for (const [k, v] of Object.entries(override)) {
    if (v === undefined) continue;

    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMerge(out[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function isNoIndex(cfg) {
  return (cfg.robots || '').toUpperCase().includes('NOINDEX');
}

/**
 * Pull FAQ entries out of a CMS "seo-tags" config (Contentful seo-schema field).
 * Accepts `faq` or `faqs`, either at the top level of the config or nested under
 * `extra`, where each entry is a { title, description } object. Returns a
 * cleaned array (only entries with a title or description) or [] when absent.
 */
function extractCmsFaqs(cfg) {
  if (!cfg) return [];

  const candidates = [cfg.faqs, cfg.faq, cfg.extra?.faqs, cfg.extra?.faq];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) {
      return candidate.filter((f) => f && (f.title || f.description));
    }
  }

  return [];
}

/**
 * Normalise a CMS `seoKeywords` value (Contentful blogPost field / seo-tags
 * item) into a meta keywords string. Accepts a comma-delimited string or an
 * array of keywords. Returns null when the field is absent or blank so the
 * page's own keywords — and ultimately DEFAULT_TAG's list — apply instead.
 */
function normaliseSeoKeywords(value) {
  const parts = Array.isArray(value) ? value : String(value ?? '').split(',');
  const cleaned = parts.map((k) => String(k ?? '').trim()).filter(Boolean);
  return cleaned.length ? cleaned.join(', ') : null;
}

// =========================
// SEO TAGS COMPONENT
// =========================
function SeoTagsHelmet({ identifier, overrides }) {
  // Initialise immediately from cache so there's no flash if already fetched
  const [feefo, setFeefo] = useState(feefoCache);

  useEffect(() => {
    if (feefoCache) return;
    loadFeefo().then((data) => setFeefo(data));
  }, []);

  const baseCfg = SeoTagsManager.getConfig(identifier);

  const safeOverrides = overrides ? { ...overrides } : null;
  if (safeOverrides?.type) delete safeOverrides.type;

  // Normalize legacy ldJson key → ldNodes
  if (
    safeOverrides?.extra?.ldJson !== undefined &&
    safeOverrides?.extra?.ldNodes === undefined
  ) {
    safeOverrides.extra.ldNodes = safeOverrides.extra.ldJson;
    delete safeOverrides.extra.ldJson;
  }

  // Contentful `seoKeywords` (top level or nested under extra) → meta keywords.
  // A blank/absent field is dropped so the page's own keywords, and failing
  // those DEFAULT_TAG's list, are used instead.
  if (safeOverrides) {
    const cmsKeywords = normaliseSeoKeywords(
      safeOverrides.seoKeywords ??
        safeOverrides.extra?.seoKeywords ??
        safeOverrides.keywords
    );
    delete safeOverrides.seoKeywords;
    if (safeOverrides.extra?.seoKeywords !== undefined) {
      safeOverrides.extra = { ...safeOverrides.extra };
      delete safeOverrides.extra.seoKeywords;
    }
    if (cmsKeywords) safeOverrides.keywords = cmsKeywords;
    else delete safeOverrides.keywords;
  }

  let cfg = deepMerge(baseCfg, safeOverrides);

  // --- ldNodes merge/replace ---
  const baseLdNodes = baseCfg?.extra?.ldNodes;
  const overrideLdNodes = safeOverrides?.extra?.ldNodes;
  const appendNodes = Boolean(safeOverrides?.extra?.appendLdNodes);

  if (overrideLdNodes !== undefined) {
    const baseArr = baseLdNodes
      ? Array.isArray(baseLdNodes)
        ? baseLdNodes
        : [baseLdNodes]
      : [];
    const overrideArr = Array.isArray(overrideLdNodes)
      ? overrideLdNodes
      : [overrideLdNodes];

    cfg = {
      ...cfg,
      extra: {
        ...(cfg.extra || {}),
        ldNodes: appendNodes ? [...baseArr, ...overrideArr] : overrideArr,
      },
    };
  }

  // --- Build @graph ---
  let graphNodes = [];

  if (!isNoIndex(cfg)) {
    // 1. Site-wide entity anchors
    graphNodes = [...SITE_GRAPH_NODES];

    // Patch organisation node with live Feefo data
    graphNodes = graphNodes.map((node) => patchSchemaWithFeefo(node, feefo));

    // 2. Auto-generated WebPage node (skip if page provides its own)
    const suppressWebPage = cfg.extra?.webPageNode === false;
    if (!suppressWebPage) {
      // Mirror the <link rel="canonical"> fallback below so pages that don't
      // set an explicit `canonical` still get their own URL here instead of
      // silently collapsing to the site root.
      const pageUrl = (
        cfg.canonical ||
        (typeof window !== 'undefined'
          ? `${BASE_URL}${window.location.pathname}`
          : BASE_URL)
      ).replace(/\/$/, '');
      graphNodes.push(buildWebPageNode(pageUrl, cfg.title, cfg.description));
    }

    // 3. Page-specific nodes
    const pageNodes = cfg.extra?.ldNodes || [];
    const pageNodesArr = Array.isArray(pageNodes) ? pageNodes : [pageNodes];
    graphNodes.push(
      ...pageNodesArr.map((schema) => patchSchemaWithFeefo(schema, feefo))
    );

    // 4. FAQ node from the CMS seo-tags config (Contentful seo-schema field).
    //    When the seo-schema carries `faq`/`faqs`, emit an FAQPage node so the
    //    questions surface as structured data for that specific page.
    const cmsFaqs = extractCmsFaqs(cfg);
    if (cmsFaqs.length) {
      const faqUrl = (cfg.canonical || BASE_URL).replace(/\/$/, '');
      graphNodes.push(buildFAQNode(faqUrl, cmsFaqs));
    }
  }

  const ldJsonBlock =
    graphNodes.length > 0
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': graphNodes,
        })
      : null;

  return (
    <Helmet>
      <title>{cfg.title}</title>
      <meta name="description" content={cfg.description} />
      {cfg.keywords && <meta name="keywords" content={cfg.keywords} />}
      <meta name="robots" content={cfg.robots || 'INDEX, FOLLOW'} />
      <link
        rel="canonical"
        href={
          cfg.canonical ||
          (typeof window !== 'undefined'
            ? `${BASE_URL}${window.location.pathname}`
            : BASE_URL)
        }
      />

      {cfg.extra?.themeColor && (
        <meta name="theme-color" content={cfg.extra.themeColor} />
      )}
      {cfg.extra?.appleIcon && (
        <link rel="apple-touch-icon" href={cfg.extra.appleIcon} />
      )}
      {cfg.extra?.ogSite && (
        <meta property="og:site_name" content={cfg.extra.ogSite} />
      )}
      {cfg.extra?.ogImage && (
        <meta property="og:image" content={cfg.extra.ogImage} />
      )}

      {cfg.extra?.siteURL && (
        <script type="text/javascript">{`var siteURL = '${cfg.extra.siteURL}';`}</script>
      )}

      {ldJsonBlock && <script type="application/ld+json">{ldJsonBlock}</script>}
    </Helmet>
  );
}

// =========================
// MANAGER
// =========================
class SeoTagsManager {
  static getConfig(identifier = '') {
    const key = resolveKey(identifier);
    const base = key ? TAGS[key] : DEFAULT_TAG;
    const out = { ...DEFAULT_TAG, ...base };
    // Auto-inject vertical schema nodes if the page has no explicit ldNodes
    if (key && VERTICAL_SCHEMA[key] && !out.extra?.ldNodes) {
      out.extra = { ...(out.extra || {}), ldNodes: VERTICAL_SCHEMA[key] };
    }
    return out;
  }

  /**
   * identifier  — route/path or tag key ("CarInsurance", "/car-insurance-quote", etc.)
   * overrides   — object from CMS (title/description/canonical/robots/keywords/extra...)
   *
   * Structured data behaviour:
   *   - NOINDEX pages: no schema output at all.
   *   - All indexed pages: a single <script type="application/ld+json"> is rendered
   *     containing a JSON-LD @graph with:
   *       1. SITE_GRAPH_NODES  — WebSite + merged Organisation entity (every page)
   *       2. WebPage node      — auto-generated from page config unless
   *                              extra.webPageNode === false (page supplies its own)
   *       3. extra.ldNodes     — any page-specific nodes (Service, BlogPosting, FAQ, etc.)
   *
   *   CMS override behaviour for ldNodes:
   *   - extra.ldNodes replaces page ldNodes by default.
   *   - extra.appendLdNodes === true appends to them instead.
   *   - extra.ldJson is accepted as a legacy alias for extra.ldNodes.
   */
  static render(identifier = '', overrides = null) {
    return <SeoTagsHelmet identifier={identifier} overrides={overrides} />;
  }
}

export default SeoTagsManager;
